import fetchWithCredentials from "apiClient";
import Student, { GradeLevelGroup } from "types/Student";
import { TeacherSubject } from "types/Teacher";

export function buildGradeLevelHierarchy(
  students: Student[]
): GradeLevelGroup[] {
  return Object.entries(
    students
      .flatMap((s) => s.subjects)
      .reduce((acc, subj) => {
        (acc[subj.gradeLevel] ||= new Set()).add(subj.subject);
        return acc;
      }, {} as Record<string, Set<string>>)
  ).map(([gradeLevel, subjects]) => ({
    gradeLevel,
    subjects: Array.from(subjects),
  }));
}

export async function fetchTeacherSubjects(): Promise<GradeLevelGroup[]> {
  const data = await fetchWithCredentials("/api/teacher/subjects/");
  if (!data) {
    return [];
  }

  const subjects = data as TeacherSubject[];
  return Object.entries(
    subjects.reduce(
      (acc: Record<string, Set<string>>, subj: TeacherSubject) => {
        (acc[subj.gradeLevel] ||= new Set()).add(subj.subject);
        return acc;
      },
      {} as Record<string, Set<string>>
    )
  ).map(([grade_level, subjects]) => ({
    gradeLevel: grade_level,
    subjects: Array.from(subjects),
  }));
}

export function mergeGradeLevelGroups(
  studentGroups: GradeLevelGroup[],
  teacherGroups: GradeLevelGroup[]
): GradeLevelGroup[] {
  const mergedGroups = new Map<string, Set<string>>();

  // Add student subjects
  studentGroups.forEach((group) => {
    const subjects = mergedGroups.get(group.gradeLevel) || new Set();
    group.subjects.forEach((subject) => subjects.add(subject));
    mergedGroups.set(group.gradeLevel, subjects);
  });

  // Add teacher subjects
  teacherGroups.forEach((group) => {
    const subjects = mergedGroups.get(group.gradeLevel) || new Set();
    group.subjects.forEach((subject) => subjects.add(subject));
    mergedGroups.set(group.gradeLevel, subjects);
  });

  return Array.from(mergedGroups.entries()).map(([gradeLevel, subjects]) => ({
    gradeLevel,
    subjects: Array.from(subjects),
  }));
}
